import React, { useEffect, useMemo, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Building2, Users, TrendingUp, Eye, MapPin, Sparkles, AlertTriangle, MessageSquare, X, Trash2, UserCheck, Edit3 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { ReassignEventModal, DeleteEventModal, EditEventModal } from "@/components/EventActionsModal";

const WORKFLOW_LABELS = {
  proposal: "Proposal", budget: "Budget", secretariat_form: "Secretariat",
  registration_school: "Reg School", registration_individual: "Reg Individual",
  committees_agendas: "Committees", vendors: "Vendors", trophies: "Trophies",
  collaborations: "Collabs", invitation_brochure: "Invitation", sponsorship_brochure: "Sponsorship",
  social_media_calendar: "Social", cash_prizes: "Prizes",
  attendance_sheet: "Attendance", marking_sheet: "Marking",
  art_craft: "Art/Craft", merchandise_goodies: "Merch",
};

export default function RPMDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showQuery, setShowQuery] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", address: "", email: "", school_name: "", school_email: "", start_date: "", end_date: "", expected_delegates: 200, delegate_fee: 2500, pm_id: "" });
  const [queryForm, setQueryForm] = useState({ step_key: "", title: "", message: "" });
  const [queries, setQueries] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users").catch(() => ({ data: [] })), api.get("/cities")]);
    setUsers(u.data); setCities(c.data);
    loadEvents("");
  };
  const loadEvents = async (city) => {
    const e = await api.get(`/events${city ? `?city=${encodeURIComponent(city)}` : ""}`);
    setEvents(e.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadEvents(filterCity); }, [filterCity]);

  const openEvent = async (ev) => {
    setSelectedEvent(ev);
    const [full, q] = await Promise.all([
      api.get(`/admin/events/${ev.event_id}/full`),
      api.get(`/events/${ev.event_id}/queries`),
    ]);
    setSummary(full.data);
    setQueries(q.data);
  };

  const submitQuery = async (e) => {
    e.preventDefault();
    await api.post(`/events/${selectedEvent.event_id}/queries`, queryForm);
    toast.success("Query raised — PM will be notified");
    setQueryForm({ step_key: "", title: "", message: "" });
    setShowQuery(false);
    const q = await api.get(`/events/${selectedEvent.event_id}/queries`);
    setQueries(q.data);
  };

  const pmList = users.filter((u) => u.role === "project_manager");
  const smList = users.filter((u) => u.role === "social_media_manager");

  return (
    <Layout navItems={getNav("regional_pm")}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader
          title={`Welcome, ${user.name.split(" ")[0]} 🌍`}
          subtitle={`Regional Project Manager${user.region ? ` · ${user.region} region` : ""} — overseeing ${pmList.length} PM${pmList.length === 1 ? "" : "s"} & ${events.length} event${events.length === 1 ? "" : "s"}`}
          icon={MapPin} accent="teal" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Project Managers" value={pmList.length} icon={Users} color="teal" />
          <StatCard label="Social Managers" value={smList.length} icon={Sparkles} color="pink" />
          <StatCard label="Events" value={events.length} icon={Building2} color="orange" />
          <StatCard label="Active" value={events.filter((e) => !e.completed).length} icon={TrendingUp} color="indigo" />
        </div>

        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="font-display font-bold text-xl">All Regional Events</h3>
          <div className="flex items-center gap-2">
            <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)} className="input-pill py-2 max-w-xs" data-testid="rpm-city-filter">
              <option value="">All cities</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-4 text-sm" data-testid="rpm-create-event-btn">+ Create Event</button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
          {events.length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No events yet for {filterCity || "your region"}.</div>}
          {events.map((e) => {
            const pm = users.find((u) => u.user_id === e.pm_id);
            const ws = e.workflow_status || {};
            const progress = Object.keys(ws).length ? Math.round(Object.values(ws).filter(Boolean).length / 17 * 100) : 0;
            return (
              <motion.div whileHover={{ y: -3 }} key={e.event_id} className="dashboard-card p-5">
                <div className="flex items-center gap-3">
                  {e.logo_url ? <img src={`${BACKEND_URL}${e.logo_url}`} className="w-12 h-12 rounded-xl object-cover" alt="" /> :
                    <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center text-white font-black">{e.name.charAt(0)}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 truncate">{e.school_name} · {pm?.name || "—"} {pm?.city ? `· ${pm.city}` : ""}</div>
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${progress}%` }} /></div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEvent(e)} className="btn-ghost text-xs py-2 flex-1" data-testid={`rpm-view-${e.event_id}`}><Eye className="w-3 h-3" /> Inspect</button>
                  <button onClick={() => nav(`/dashboard/events/${e.event_id}`)} className="btn-primary text-xs py-2 flex-1">Open</button>
                  <button onClick={() => setEditTarget(e)} className="btn-ghost text-xs py-2 px-2 text-indigo-600 hover:bg-indigo-50" data-testid={`rpm-edit-${e.event_id}`} title="Edit event details">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setReassignTarget(e)} className="btn-ghost text-xs py-2 px-2 text-orange-600 hover:bg-orange-50" data-testid={`rpm-reassign-${e.event_id}`} title="Reassign to a different PM">
                    <UserCheck className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleteTarget(e)} className="btn-ghost text-xs py-2 px-2 text-rose-600 hover:bg-rose-50" data-testid={`rpm-delete-${e.event_id}`} title="Delete event">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {selectedEvent && summary && (
        <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto">
          <div className="min-h-screen p-4 flex items-start justify-center relative">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-center gap-3">
                <h3 className="font-display font-black text-2xl flex-1">{selectedEvent.name}</h3>
                <button onClick={() => setShowQuery(true)} className="btn-primary text-sm py-2" data-testid="raise-query-btn"><AlertTriangle className="w-4 h-4" /> Raise Query</button>
                <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="rpm-evdetail-close"><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Box label="Committees" value={summary.committees.length} />
                  <Box label="Allotments" value={summary.allotments.length} />
                  <Box label="Paid" value={summary.allotments.filter((a) => a.paid).length} />
                  <Box label="Net Profit" value={`₹${summary.budget.net_profit.toLocaleString()}`} />
                </div>

                <Section title="Workflow status">
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-1.5 text-xs">
                    {Object.entries(WORKFLOW_LABELS).map(([k, v]) => (
                      <div key={k} className={`p-2 rounded-lg text-center ${selectedEvent.workflow_status?.[k] ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{v}</div>
                    ))}
                  </div>
                </Section>

                <Section title="Committees breakdown">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100"><th>Committee</th><th>Alloted</th><th>Paid</th><th>Unpaid</th></tr></thead>
                    <tbody>
                      {summary.committees.map((c) => {
                        const a = summary.allotments.filter((x) => x.committee_id === c.committee_id);
                        return <tr key={c.committee_id} className="border-b border-slate-50"><td className="py-2">{c.name}</td><td>{a.length}</td><td className="text-green-600">{a.filter((x) => x.paid).length}</td><td className="text-amber-600">{a.filter((x) => !x.paid).length}</td></tr>;
                      })}
                    </tbody>
                  </table>
                </Section>

                <Section title="Budget overview">
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="p-3 rounded-xl bg-emerald-50"><div className="text-xs text-emerald-700">Revenue</div><div className="text-xl font-black text-emerald-700">₹{summary.budget.total_revenue.toLocaleString()}</div></div>
                    <div className="p-3 rounded-xl bg-rose-50"><div className="text-xs text-rose-700">Expenses</div><div className="text-xl font-black text-rose-700">₹{summary.budget.total_expenses.toLocaleString()}</div></div>
                    <div className="p-3 rounded-xl bg-orange-50"><div className="text-xs text-orange-700">Kaimera</div><div className="text-xl font-black brand-gradient-text">₹{summary.budget.kaimera_total.toLocaleString()}</div></div>
                  </div>
                </Section>

                <Section title={`Queries raised (${queries.length})`}>
                  {queries.length === 0 && <div className="text-sm text-slate-400">No queries on this event.</div>}
                  {queries.map((q) => (
                    <div key={q.query_id} className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="flex justify-between"><div className="font-bold text-amber-800">{q.title}</div><span className={`text-xs role-pill ${q.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{q.status}</span></div>
                      <div className="text-sm text-amber-700">{q.message}</div>
                      {q.pm_response && <div className="text-xs mt-2 p-2 rounded bg-white text-slate-700"><b>PM:</b> {q.pm_response}</div>}
                    </div>
                  ))}
                </Section>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {showQuery && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <ModalCloseBtn onClose={() => setShowQuery(false)} />
            <h3 className="font-display font-black text-2xl mb-1">Raise a query</h3>
            <p className="text-sm text-slate-500 mb-4">The PM will see this on their dashboard.</p>
            <form onSubmit={submitQuery} className="space-y-3">
              <select className="input-pill" value={queryForm.step_key} onChange={(e) => setQueryForm({ ...queryForm, step_key: e.target.value })} data-testid="query-step">
                <option value="">Step (optional)</option>
                {Object.entries(WORKFLOW_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input required placeholder="Query title" className="input-pill" value={queryForm.title} onChange={(e) => setQueryForm({ ...queryForm, title: e.target.value })} data-testid="query-title" />
              <textarea required rows={4} placeholder="What needs attention?" className="input-pill" value={queryForm.message} onChange={(e) => setQueryForm({ ...queryForm, message: e.target.value })} data-testid="query-message" />
              <button type="submit" className="btn-primary w-full py-3" data-testid="query-submit">Send to PM</button>
            </form>
          </motion.div>
        </div>
      )}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto relative" data-testid="rpm-create-modal">
            <ModalCloseBtn onClose={() => setShowCreate(false)} />
            <h3 className="font-display font-black text-2xl mb-1">Create event (RPM)</h3>
            <p className="text-xs text-slate-500 mb-4">You must assign a Project Manager — they'll own the day-to-day workflow.</p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!createForm.pm_id) return toast.error("Please select a Project Manager");
                try {
                  const r = await api.post("/events", { ...createForm, expected_delegates: parseInt(createForm.expected_delegates) || 200, delegate_fee: parseFloat(createForm.delegate_fee) || 2500 });
                  toast.success(`Event "${r.data.name}" created and assigned`);
                  setShowCreate(false);
                  setCreateForm({ name: "", address: "", email: "", school_name: "", school_email: "", start_date: "", end_date: "", expected_delegates: 200, delegate_fee: 2500, pm_id: "" });
                  loadEvents(filterCity);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Failed");
                }
              }}
              className="space-y-3"
            >
              <input required placeholder="Event name" className="input-pill" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="rpm-ev-name" />
              <input required placeholder="Address" className="input-pill" value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} />
              <input required type="email" placeholder="Event email" className="input-pill" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
              <input required placeholder="Host school name" className="input-pill" value={createForm.school_name} onChange={(e) => setCreateForm({ ...createForm, school_name: e.target.value })} />
              <input type="email" placeholder="School email (optional)" className="input-pill" value={createForm.school_email} onChange={(e) => setCreateForm({ ...createForm, school_email: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <input required type="date" className="input-pill" value={createForm.start_date} onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })} />
                <input required type="date" className="input-pill" value={createForm.end_date} onChange={(e) => setCreateForm({ ...createForm, end_date: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Expected delegates" className="input-pill" value={createForm.expected_delegates} onChange={(e) => setCreateForm({ ...createForm, expected_delegates: e.target.value })} />
                <input type="number" placeholder="Delegate fee (₹)" className="input-pill" value={createForm.delegate_fee} onChange={(e) => setCreateForm({ ...createForm, delegate_fee: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">Assign Project Manager (required)</label>
                <select required className="input-pill" value={createForm.pm_id} onChange={(e) => setCreateForm({ ...createForm, pm_id: e.target.value })} data-testid="rpm-ev-pm">
                  <option value="">Select a PM in your region…</option>
                  {users.filter((u) => u.role === "project_manager" && (!user.region || !u.region || u.region === user.region)).map((p) => (
                    <option key={p.user_id} value={p.user_id}>{p.name} · {p.city || "—"} · {p.email}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost flex-1 py-3">Cancel</button>
                <button type="submit" className="btn-primary flex-1 py-3" data-testid="rpm-ev-submit">Create event</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteEventModal
          event={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => loadEvents(filterCity)}
        />
      )}

      {reassignTarget && (
        <ReassignEventModal
          event={reassignTarget}
          projectManagers={users.filter((u) => u.role === "project_manager")}
          onClose={() => setReassignTarget(null)}
          onDone={() => loadEvents(filterCity)}
        />
      )}

      {editTarget && (
        <EditEventModal event={editTarget} onClose={() => setEditTarget(null)} onDone={() => loadEvents(filterCity)} />
      )}
    </Layout>
  );
}

function Box({ label, value }) { return <div className="p-3 rounded-xl bg-orange-50"><div className="text-xs text-orange-700 font-bold uppercase">{label}</div><div className="text-lg font-display font-black brand-gradient-text">{value}</div></div>; }
function Section({ title, children }) { return <div><h4 className="font-display font-bold text-lg mb-3">{title}</h4><div className="space-y-2">{children}</div></div>; }
